/**
 * 수증기(Steam) 사이징.
 *
 * 근거: API Standard 520 Part I, 10판(2020) 5.7절
 *   - 5.7.1 식 (26)  SI 소요면적  A = 190.5·W / (P1·Kd·Kb·Kc·KN·KSH)
 *   - 5.7.1 식 (27)  KN = 1.0                      (P1 ≤ 10,339 kPa)
 *   - 5.7.1 식 (29)  KN = (0.02764·P1 − 1000) / (0.03324·P1 − 1061)
 *                                                   (10,339 < P1 ≤ 22,057 kPa, SI)
 *   - 5.7.1          KSH: 포화증기는 1.0, 과열증기는 Table 13 (SI)
 *                         1200 °F(약 649 ℃) 초과에서는 기체 사이징식 (6)~(11) 을 쓸 것
 *   - 5.7.1          Kb 는 밸런스드 벨로우즈에만 적용. 일반형은 1.0
 *
 * 단위는 SI: 유량 kg/h · 압력 kPa(절대) · 온도 ℃ · 면적 mm²
 */
import { lookupKsh, KSH_MAX_TEMPERATURE_C } from "../data/ksh";
import { KD_PRELIMINARY_PRV, type ValveType } from "./gas";

/** 식 (27)/(29) 의 경계 압력. 1500 psia 에 해당. */
export const KN_LOWER_LIMIT_KPA = 10_339;
/** 식 (29) 의 상한. 3200 psia 에 해당. */
export const KN_UPPER_LIMIT_KPA = 22_057;

export interface SteamSizingInput {
  /** W — 소요 배출 유량 (kg/h) */
  readonly requiredFlowKgH: number;
  /** P1 — 배출 압력 (kPa 절대). 설정압 + 허용 과압 + 대기압. */
  readonly relievingPressureKpaAbs: number;
  /** 포화증기인지 여부. 포화면 KSH = 1.0 이고 온도는 쓰이지 않는다. */
  readonly saturated: boolean;
  /** 과열증기일 때의 배출 온도 (℃). 포화면 생략 가능. */
  readonly relievingTemperatureC?: number;
  readonly valveType: ValveType;
  /** Kd — 생략 시 예비 사이징값 0.975 */
  readonly dischargeCoefficient?: number;
  /** Kb — 밸런스드 벨로우즈에만 적용. 제작사 자료에서 얻는다. */
  readonly bellowsBackpressureCorrection?: number;
  /** Kc — 파열판 없음 1.0 / 인증값 없는 조합 0.9 */
  readonly combinationCorrection?: number;
  /**
   * KSH 를 직접 지정할 때 사용. 지정하면 Table 13 조회를 건너뛴다.
   * 제작사·사내 기준값을 써야 하는 경우를 위한 탈출구다.
   */
  readonly superheatCorrectionOverride?: number;
}

export interface SteamSizingSuccess {
  readonly ok: true;
  readonly requiredAreaMm2: number;
  readonly equation: "API 520 Part I (26)";
  readonly intermediates: {
    /** KN — Napier 식 보정 (식 27 또는 29) */
    readonly kn: number;
    readonly knEquation: "API 520 Part I (27)" | "API 520 Part I (29)";
    /** KSH — 과열 보정 */
    readonly ksh: number;
    /** KSH 를 표에서 보간했는지 (격자점에 정확히 맞으면 false) */
    readonly kshInterpolated: boolean;
    readonly kshSource: "saturated" | "table-13" | "override";
    readonly kd: number;
    readonly kb: number;
    readonly kc: number;
  };
  readonly warnings: readonly string[];
}

export interface SteamSizingFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "out-of-range" | "use-gas-equation";
  readonly message: string;
}

export type SteamSizingResult = SteamSizingSuccess | SteamSizingFailure;

/**
 * Napier 식 보정계수 KN — API 520 Part I 식 (27)/(29), SI 단위.
 *
 * @param p1KpaAbs 배출 압력 (kPa 절대)
 */
export function napierCorrection(p1KpaAbs: number): {
  kn: number;
  equation: "API 520 Part I (27)" | "API 520 Part I (29)";
} {
  if (p1KpaAbs <= KN_LOWER_LIMIT_KPA) {
    return { kn: 1.0, equation: "API 520 Part I (27)" };
  }
  return {
    kn:
      (0.02764 * p1KpaAbs - 1000) / (0.03324 * p1KpaAbs - 1061),
    equation: "API 520 Part I (29)",
  };
}

function invalid(
  reason: SteamSizingFailure["reason"],
  message: string,
): SteamSizingFailure {
  return { ok: false, reason, message };
}

/** 수증기 소요 배출 면적을 계산한다. */
export function sizeSteam(input: SteamSizingInput): SteamSizingResult {
  const {
    requiredFlowKgH: W,
    relievingPressureKpaAbs: P1,
    saturated,
    valveType,
  } = input;

  if (!Number.isFinite(W) || W <= 0) {
    return invalid("invalid-input", "소요 유량 W 는 0보다 커야 합니다.");
  }
  if (!Number.isFinite(P1) || P1 <= 0) {
    return invalid("invalid-input", "배출 압력 P1 은 0보다 커야 합니다.");
  }
  if (P1 > KN_UPPER_LIMIT_KPA) {
    return invalid(
      "out-of-range",
      `배출 압력 ${P1.toFixed(0)} kPa 는 식 (29) 의 적용 상한 ` +
        `${KN_UPPER_LIMIT_KPA} kPa(3200 psia)를 넘습니다.`,
    );
  }

  const Kd = input.dischargeCoefficient ?? KD_PRELIMINARY_PRV;
  const Kc = input.combinationCorrection ?? 1.0;
  if (!Number.isFinite(Kd) || Kd <= 0) return invalid("invalid-input", "Kd 가 올바르지 않습니다.");
  if (!Number.isFinite(Kc) || Kc <= 0) return invalid("invalid-input", "Kc 가 올바르지 않습니다.");

  const warnings: string[] = [];

  // ── Kb: 벨로우즈에만 적용 (5.7.1) ───────────────────────────
  let Kb = 1.0;
  if (valveType === "balanced-bellows") {
    if (input.bellowsBackpressureCorrection === undefined) {
      warnings.push(
        "밸런스드 벨로우즈인데 Kb 가 입력되지 않아 1.0 으로 계산했습니다. " +
          "제작사 자료에서 확인해 입력해야 합니다.",
      );
    } else {
      Kb = input.bellowsBackpressureCorrection;
      if (!Number.isFinite(Kb) || Kb <= 0 || Kb > 1) {
        return invalid("invalid-input", "Kb 는 0 초과 1 이하이어야 합니다.");
      }
    }
  } else if (input.bellowsBackpressureCorrection !== undefined) {
    warnings.push(
      "일반형·파일럿형에는 Kb 가 적용되지 않습니다(5.7.1). 1.0 으로 계산했습니다.",
    );
  }

  // ── KSH ─────────────────────────────────────────────────────
  let KSH: number;
  let kshInterpolated = false;
  let kshSource: SteamSizingSuccess["intermediates"]["kshSource"];

  if (input.superheatCorrectionOverride !== undefined) {
    KSH = input.superheatCorrectionOverride;
    kshSource = "override";
    if (!Number.isFinite(KSH) || KSH <= 0 || KSH > 1) {
      return invalid("invalid-input", "KSH 는 0 초과 1 이하이어야 합니다.");
    }
  } else if (saturated) {
    KSH = 1.0;
    kshSource = "saturated";
  } else {
    const T = input.relievingTemperatureC;
    if (T === undefined || !Number.isFinite(T)) {
      return invalid(
        "invalid-input",
        "과열증기는 배출 온도(℃)가 있어야 KSH 를 구할 수 있습니다.",
      );
    }
    if (T > KSH_MAX_TEMPERATURE_C) {
      return invalid(
        "use-gas-equation",
        `배출 온도 ${T} ℃ 는 1200 °F(약 ${KSH_MAX_TEMPERATURE_C} ℃)를 넘습니다. ` +
          "API 520 Part I 5.7.1 에 따라 수증기식이 아니라 기체 사이징식으로 계산해야 합니다.",
      );
    }
    const found = lookupKsh(P1 / 1000, T);
    if (!found.ok) {
      return invalid("out-of-range", found.message);
    }
    KSH = found.ksh;
    kshInterpolated = found.interpolated;
    kshSource = "table-13";
  }

  const { kn: KN, equation: knEquation } = napierCorrection(P1);

  // 식 (26): A = 190.5·W / (P1·Kd·Kb·Kc·KN·KSH)
  const requiredAreaMm2 = (190.5 * W) / (P1 * Kd * Kb * Kc * KN * KSH);

  return {
    ok: true,
    requiredAreaMm2,
    equation: "API 520 Part I (26)",
    intermediates: {
      kn: KN,
      knEquation,
      ksh: KSH,
      kshInterpolated,
      kshSource,
      kd: Kd,
      kb: Kb,
      kc: Kc,
    },
    warnings,
  };
}
